import React, { useState, useCallback, useContext, useEffect, useRef, Suspense } from "react";
import { useParams } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles } from "@material-ui/core/styles";
import TicketsManagerTabs from "../../components/TicketsManagerTabs";
import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { CircularProgress } from "@material-ui/core";

const Ticket = React.lazy(() => import("../../components/Ticket"));

const defaultTicketsManagerRatio = 0.34;
const fallbackTicketsManagerWidth = 380;
const minTicketsManagerWidth = 320;
const maxTicketsManagerWidth = 480;
const minMessagesWidth = 560;
const keyboardResizeStep = 16;

const getWidthLimits = (containerWidth) => {
	const availableWidth = Number(containerWidth);
	const responsiveMaximum = Number.isFinite(availableWidth) && availableWidth > 0
		? availableWidth - minMessagesWidth
		: maxTicketsManagerWidth;

	return {
		min: minTicketsManagerWidth,
		max: Math.max(
			minTicketsManagerWidth,
			Math.min(maxTicketsManagerWidth, responsiveMaximum)
		),
	};
};

const normalizeTicketsManagerWidth = (value, containerWidth) => {
	const numericValue = Number(value);
	const limits = getWidthLimits(containerWidth);
	const preferredValue = Number.isFinite(numericValue) && numericValue > 0
		? numericValue
		: fallbackTicketsManagerWidth;

	return Math.round(Math.max(limits.min, Math.min(limits.max, preferredValue)));
};

const getAdaptiveDefaultWidth = (containerWidth) => {
	const availableWidth = Number(containerWidth);
	const preferredWidth = Number.isFinite(availableWidth) && availableWidth > 0
		? availableWidth * defaultTicketsManagerRatio
		: fallbackTicketsManagerWidth;

	return normalizeTicketsManagerWidth(preferredWidth, availableWidth);
};

const useStyles = makeStyles((theme) => ({
	chatContainer: {
		flex: 1,
		padding: "2px",
		height: "calc(100% - 48px)",
		overflowY: "hidden",
		minHeight: 0,
	},
	chatPapper: {
		display: "flex",
		height: "100%",
		minWidth: 0,
	},
	contactsWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		flexShrink: 0,
		overflowY: "hidden",
		position: "relative",
		minWidth: `${minTicketsManagerWidth}px`,
		[theme.breakpoints.down("sm")]: {
			minWidth: "100%",
			width: "100% !important",
			maxWidth: "100% !important",
		},
	},
	messagesWrapper: {
		display: "flex",
		height: "100%",
		minHeight: 0,
		minWidth: 0,
		overflowY: "hidden",
		flexDirection: "column",
		flexGrow: 1,
		[theme.breakpoints.down("sm")]: {
			display: (props) => props.ticketId ? "flex" : "none",
			position: "fixed",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			zIndex: 1200,
			backgroundColor: theme.palette.background?.default || "#fff",
		},
	},
	welcomeMsg: {
		background: theme.palette.tabHeaderBackground,
		display: "flex",
		justifyContent: "space-evenly",
		alignItems: "center",
		height: "100%",
		textAlign: "center",
	},
	dragger: {
		width: "14px",
		cursor: "col-resize",
		padding: 0,
		position: "absolute",
		top: 0,
		right: -7,
		bottom: 0,
		zIndex: 110,
		backgroundColor: "transparent",
		userSelect: "none",
		touchAction: "none",
		outline: "none",
		transition: "background-color 0.2s ease",
		"&::after": {
			content: '""',
			position: "absolute",
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%)",
			width: 3,
			height: 44,
			borderRadius: 4,
			backgroundColor: theme.palette.type === "dark"
				? "rgba(255,255,255,0.34)"
				: "rgba(0,0,0,0.24)",
			boxShadow: theme.palette.type === "dark"
				? "0 0 0 1px rgba(0,0,0,0.18)"
				: "0 0 0 1px rgba(255,255,255,0.55)",
			transition: "height 0.2s ease, background-color 0.2s ease",
		},
		"&:hover, &:focus-visible": {
			backgroundColor: theme.palette.type === "dark"
				? "rgba(255,255,255,0.06)"
				: "rgba(0,0,0,0.04)",
			"&::after": {
				height: 58,
				backgroundColor: theme.palette.primary.main,
			},
		},
		[theme.breakpoints.down("sm")]: {
			display: "none",
		},
	},
	logo: {
		logo: theme.logo,
		content: "url(" + (theme.mode === "light"
			? theme.calculatedLogoLight()
			: theme.calculatedLogoDark()) + ")",
	},
}));

const TicketsCustom = () => {
	const { user } = useContext(AuthContext);
	const { ticketId } = useParams();
	const [ticketsManagerWidth, setTicketsManagerWidth] = useState(fallbackTicketsManagerWidth);
	const [containerWidth, setContainerWidth] = useState(0);

	const classes = useStyles({ ticketsManagerWidth, ticketId });
	const chatPaperRef = useRef(null);
	const ticketsManagerWidthRef = useRef(ticketsManagerWidth);
	const initializedWidthRef = useRef(false);
	const dragStateRef = useRef({ active: false, pointerId: null });

	useEffect(() => {
		initializedWidthRef.current = false;
		const container = chatPaperRef.current;
		if (!container) return undefined;

		const updateWidthForContainer = () => {
			const measuredWidth = container.getBoundingClientRect().width;
			if (!measuredWidth) return;

			setContainerWidth(measuredWidth);
			setTicketsManagerWidth((currentWidth) => {
				let nextWidth;

				if (!initializedWidthRef.current) {
					const storedWidth = Number(user?.defaultTicketsManagerWidth);
					const hasCurrentPreference = Number.isFinite(storedWidth)
						&& storedWidth >= minTicketsManagerWidth
						&& storedWidth <= maxTicketsManagerWidth;

					nextWidth = hasCurrentPreference
						? normalizeTicketsManagerWidth(storedWidth, measuredWidth)
						: getAdaptiveDefaultWidth(measuredWidth);
					initializedWidthRef.current = true;
				} else {
					nextWidth = normalizeTicketsManagerWidth(currentWidth, measuredWidth);
				}

				ticketsManagerWidthRef.current = nextWidth;
				return nextWidth;
			});
		};

		updateWidthForContainer();

		if (typeof ResizeObserver !== "undefined") {
			const observer = new ResizeObserver(updateWidthForContainer);
			observer.observe(container);
			return () => observer.disconnect();
		}

		window.addEventListener("resize", updateWidthForContainer);
		return () => window.removeEventListener("resize", updateWidthForContainer);
	}, [user?.id, user?.defaultTicketsManagerWidth]);

	useEffect(() => () => {
		document.body.style.cursor = "";
		document.body.style.userSelect = "";
	}, []);

	const handleSaveContact = useCallback(async (value) => {
		const measuredWidth = chatPaperRef.current?.getBoundingClientRect().width || containerWidth;
		const validValue = normalizeTicketsManagerWidth(value, measuredWidth);

		try {
			await api.put(`/users/toggleChangeWidht/${user.id}`, {
				defaultTicketsManagerWidth: validValue,
			});
			ticketsManagerWidthRef.current = validValue;
			setTicketsManagerWidth(validValue);
		} catch (error) {
			console.error("Erro ao salvar largura:", error);
		}
	}, [containerWidth, user?.id]);

	const handlePointerDown = useCallback((event) => {
		if (event.pointerType === "mouse" && event.button !== 0) return;

		event.preventDefault();
		event.currentTarget.setPointerCapture?.(event.pointerId);
		dragStateRef.current = { active: true, pointerId: event.pointerId };
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
	}, []);

	const handlePointerMove = useCallback((event) => {
		if (!dragStateRef.current.active) return;
		const container = chatPaperRef.current;
		if (!container) return;

		const containerBounds = container.getBoundingClientRect();
		const newWidth = normalizeTicketsManagerWidth(
			event.clientX - containerBounds.left,
			containerBounds.width
		);

		ticketsManagerWidthRef.current = newWidth;
		setTicketsManagerWidth(newWidth);
	}, []);

	const finishPointerResize = useCallback((event, saveWidth = true) => {
		if (!dragStateRef.current.active) return;

		dragStateRef.current = { active: false, pointerId: null };
		if (event?.currentTarget?.hasPointerCapture?.(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId);
		}
		document.body.style.cursor = "";
		document.body.style.userSelect = "";

		if (saveWidth) handleSaveContact(ticketsManagerWidthRef.current);
	}, [handleSaveContact]);

	const handleKeyDown = useCallback((event) => {
		const containerBounds = chatPaperRef.current?.getBoundingClientRect();
		if (!containerBounds) return;

		let nextWidth = ticketsManagerWidthRef.current;
		if (event.key === "ArrowLeft") nextWidth -= keyboardResizeStep;
		else if (event.key === "ArrowRight") nextWidth += keyboardResizeStep;
		else if (event.key === "Home") nextWidth = getAdaptiveDefaultWidth(containerBounds.width);
		else return;

		event.preventDefault();
		nextWidth = normalizeTicketsManagerWidth(nextWidth, containerBounds.width);
		ticketsManagerWidthRef.current = nextWidth;
		setTicketsManagerWidth(nextWidth);
	}, []);

	const handleKeyUp = useCallback((event) => {
		if (["ArrowLeft", "ArrowRight", "Home"].includes(event.key)) {
			handleSaveContact(ticketsManagerWidthRef.current);
		}
	}, [handleSaveContact]);

	const handleResetWidth = useCallback(() => {
		const measuredWidth = chatPaperRef.current?.getBoundingClientRect().width || containerWidth;
		const defaultWidth = getAdaptiveDefaultWidth(measuredWidth);
		ticketsManagerWidthRef.current = defaultWidth;
		setTicketsManagerWidth(defaultWidth);
		handleSaveContact(defaultWidth);
	}, [containerWidth, handleSaveContact]);

	const widthLimits = getWidthLimits(containerWidth);
	const effectiveWidth = normalizeTicketsManagerWidth(ticketsManagerWidth, containerWidth);

	return (
		<QueueSelectedProvider>
			<div className={classes.chatContainer}>
				<div className={classes.chatPapper} ref={chatPaperRef}>
					<div
						className={classes.contactsWrapper}
						style={{
							width: `${effectiveWidth}px`,
							minWidth: `${widthLimits.min}px`,
							maxWidth: `${widthLimits.max}px`,
							opacity: effectiveWidth > 0 ? 1 : 0,
							visibility: effectiveWidth > 0 ? "visible" : "hidden",
						}}
					>
						<TicketsManagerTabs />
						<div
							onPointerDown={handlePointerDown}
							onPointerMove={handlePointerMove}
							onPointerUp={(event) => finishPointerResize(event, true)}
							onPointerCancel={(event) => finishPointerResize(event, false)}
							onDoubleClick={handleResetWidth}
							onKeyDown={handleKeyDown}
							onKeyUp={handleKeyUp}
							className={classes.dragger}
							title="Arraste para ajustar. Clique duas vezes para restaurar."
							role="separator"
							tabIndex={0}
							aria-orientation="vertical"
							aria-valuemin={widthLimits.min}
							aria-valuemax={widthLimits.max}
							aria-valuenow={effectiveWidth}
							aria-label="Ajustar a largura da lista de atendimentos"
						/>
					</div>
					<div className={classes.messagesWrapper}>
						{ticketId ? (
							<Suspense fallback={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}><CircularProgress /></div>}>
								<Ticket />
							</Suspense>
						) : (
							<Hidden only={["sm", "xs"]}>
								<Paper square variant="outlined" className={classes.welcomeMsg}>
									<span>
										<center>
											<img className={classes.logo} width="50%" alt="" />
										</center>
										{i18n.t("chat.noTicketMessage")}
									</span>
								</Paper>
							</Hidden>
						)}
					</div>
				</div>
			</div>
		</QueueSelectedProvider>
	);
};

export default TicketsCustom;
